import React, { type ErrorInfo, type ReactNode } from 'react';
import { Button, Card, Heading } from './ui';

type ErrorBoundaryProps = {
  children: ReactNode;
  /**
   * Optional hooks for testing/embedding. Defaults to window navigation actions.
   */
  onReload?: () => void;
  onGoHome?: () => void;
};

type ErrorBoundaryState = {
  error: unknown;
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    void import('../lib/sentry')
      .then(({ captureException }) => {
        captureException(error, {
          componentStack: errorInfo.componentStack,
        });
      })
      .catch(() => {
        // Swallow reporting failures so the fallback UI can still render.
      });
  }

  render() {
    if (this.state.error) {
      return (
        <ErrorFallback
          error={this.state.error}
          onReload={this.props.onReload ?? (() => window.location.reload())}
          onGoHome={this.props.onGoHome ?? (() => window.location.assign('/'))}
        />
      );
    }

    return this.props.children;
  }
}

function ErrorFallback({
  error,
  onReload,
  onGoHome,
}: {
  error?: unknown;
  onReload: () => void;
  onGoHome: () => void;
}) {
  return (
    <main className="min-h-screen bg-st-dark text-st-white flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <Card hoverEffect={false} className="text-center">
          <Heading level={2} className="mb-3">
            Something went wrong
          </Heading>
          <p className="text-st-muted mb-6">
            We hit an unexpected issue. You can reload the page or return home.
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <Button type="button" variant="primary" onClick={onReload}>
              Reload
            </Button>
            <Button type="button" variant="outline" onClick={onGoHome}>
              Go Home
            </Button>
          </div>

          {import.meta.env.DEV && error ? (
            <pre className="mt-6 text-left text-xs text-slate-300 bg-black/20 rounded-md p-4 overflow-auto max-h-64">
              {error instanceof Error ? `${error.name}: ${error.message}` : String(error)}
            </pre>
          ) : null}
        </Card>
      </div>
    </main>
  );
}


